import React from 'react';
import { Edit2, Save, X } from 'lucide-react';

/**
 * Presentational primitives for the Assessment Analysis page. Pure UI — no
 * data logic lives here. Surfaces follow the Lead Overview dark-mode layering:
 * gray-900 cards, gray-800 tiles, gray-700 chips/inputs, so each level stays legible.
 */

export const CARD =
  'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';
export const SUBCARD =
  'rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800';
export const INPUT =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400';
export const ICON_BTN =
  'p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
export const DANGER_ICON_BTN =
  'p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors';
export const DASHED_ADD =
  'flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:text-gray-900 hover:border-gray-400 dark:text-gray-400 dark:hover:text-white dark:hover:border-gray-500 transition-colors';

export const TONES = {
  red: {
    tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    text: 'text-red-600 dark:text-red-400',
    pill: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    bar: 'bg-red-500',
    seg: 'bg-red-400/70 dark:bg-red-500/60',
  },
  amber: {
    tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    text: 'text-amber-600 dark:text-amber-400',
    pill: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
    bar: 'bg-amber-500',
    seg: 'bg-amber-400/70 dark:bg-amber-500/60',
  },
  green: {
    tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    text: 'text-emerald-600 dark:text-emerald-400',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800',
    bar: 'bg-emerald-500',
    seg: 'bg-emerald-400/70 dark:bg-emerald-500/60',
  },
  blue: {
    tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    text: 'text-blue-600 dark:text-blue-400',
    pill: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    bar: 'bg-blue-500',
    seg: 'bg-blue-400/70 dark:bg-blue-500/60',
  },
  purple: {
    tile: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    text: 'text-purple-600 dark:text-purple-400',
    pill: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
    bar: 'bg-purple-500',
    seg: 'bg-purple-400/70 dark:bg-purple-500/60',
  },
  gray: {
    tile: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200',
    text: 'text-gray-700 dark:text-gray-300',
    pill: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
    bar: 'bg-gray-400',
    seg: 'bg-gray-300 dark:bg-gray-600',
  },
};

export const IconTile = ({ icon: Icon, emoji, tone = 'red', size = 'md', plain = false, className = '' }) => {
  const dims = size === 'lg' ? 'w-12 h-12 text-2xl' : size === 'sm' ? 'w-8 h-8 text-base' : 'w-10 h-10 text-xl';
  const iconCls = size === 'lg' ? 'w-6 h-6' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const surface = plain
    ? 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white'
    : TONES[tone]?.tile || TONES.red.tile;
  return (
    <div className={`${dims} rounded-xl flex items-center justify-center flex-shrink-0 ${surface} ${className}`}>
      {Icon ? <Icon className={iconCls} /> : <span className="leading-none">{emoji}</span>}
    </div>
  );
};

// Emoji sits inline to the left of the title with no tile; `left`/`right` are absolute slots.
export const SectionHeader = ({ icon: Icon, emoji, title, subtitle, left, right, className = '' }) => (
  <div className={`relative flex flex-col items-center text-center mb-6 ${className}`}>
    <h2 className="flex items-center justify-center gap-2.5 text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
      {emoji && <span className="text-2xl leading-none" aria-hidden="true">{emoji}</span>}
      {Icon && !emoji && <Icon className="w-5 h-5 text-red-600 dark:text-red-400" />}
      <span>{title}</span>
    </h2>
    {subtitle && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-xl">{subtitle}</p>}
    {left && <div className="absolute left-0 top-0">{left}</div>}
    {right && <div className="absolute right-0 top-0">{right}</div>}
  </div>
);

export const EditToolbar = ({ editing, onEdit, onSave, onCancel, label = 'Edit' }) =>
  editing ? (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onSave}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
        title="Save changes"
      >
        <Save className="w-3.5 h-3.5" />
        Save
      </button>
      <button type="button" onClick={onCancel} className={ICON_BTN} title="Cancel editing">
        <X className="w-4 h-4" />
      </button>
    </div>
  ) : (
    <button
      type="button"
      onClick={onEdit}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      title={label}
    >
      <Edit2 className="w-3.5 h-3.5" />
      Edit
    </button>
  );

export const StatTile = ({ label, value, tone }) => (
  <div className={`${SUBCARD} p-4 text-center`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`mt-1 text-2xl font-bold tracking-tight ${tone ? TONES[tone].text : 'text-gray-900 dark:text-white'}`}>
      {value}
    </p>
  </div>
);

export const ScoreRing = ({ value = 0, size = 168, stroke = 12 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-gray-200 dark:stroke-gray-700" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          stroke="#dc2626"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct / 100)}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white">{Math.round(pct)}</span>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">out of 100</span>
      </div>
    </div>
  );
};

/**
 * Compact KPI card with a colour-banded threshold bar and a marker.
 * `segments` are [{ to, tone }] in value units, `ticks` are [{ at, label }].
 * `value` null/undefined renders the empty state.
 */
export const ThresholdMetric = ({ icon, emoji, label, value, display, tone = 'gray', statusLabel, segments = [], scaleMax = 100, ticks = [] }) => {
  const hasValue = value !== null && value !== undefined && !Number.isNaN(value);
  const markerPct = hasValue ? Math.max(0, Math.min(100, (value / scaleMax) * 100)) : 0;
  return (
    <div className={`${CARD} p-6 flex flex-col items-center text-center`}>
      <p className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
        {emoji && <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>}
        {icon && !emoji && <IconTile icon={icon} tone={hasValue ? tone : 'gray'} size="sm" />}
        <span>{label}</span>
      </p>
      {hasValue ? (
        <>
          <p className={`mt-1 text-3xl font-bold tracking-tight ${TONES[tone].text}`}>{display}</p>
          {statusLabel && (
            <span className={`mt-2 inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${TONES[tone].pill}`}>
              {statusLabel}
            </span>
          )}
          <div className="w-full mt-5">
            <div className="relative">
              <div className="h-2.5 rounded-full overflow-hidden flex">
                {segments.map((s, i) => {
                  const from = i === 0 ? 0 : segments[i - 1].to;
                  const width = ((s.to - from) / scaleMax) * 100;
                  return <div key={i} style={{ width: `${width}%` }} className={`h-full ${TONES[s.tone].seg}`} />;
                })}
              </div>
              <div
                className="absolute top-1/2 w-4 h-4 rounded-full bg-white dark:bg-gray-900 border-[3px] border-gray-900 dark:border-white shadow -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${markerPct}%` }}
              />
            </div>
            <div className="relative mt-2 h-4 text-[11px] text-gray-500 dark:text-gray-400">
              {ticks.map((t, i) => {
                const pct = (t.at / scaleMax) * 100;
                const pos = pct <= 0 ? 'left-0' : pct >= 100 ? 'right-0' : '-translate-x-1/2';
                return (
                  <span key={i} className={`absolute ${pos}`} style={pct > 0 && pct < 100 ? { left: `${pct}%` } : undefined}>
                    {t.label}
                  </span>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <p className="mt-3 text-sm italic text-gray-400 dark:text-gray-500">No data available</p>
      )}
    </div>
  );
};

// With `onSelect`, the cards double as the selector for a preview below (active one is outlined in red).
export const StepCards = ({ steps, activeIndex = null, onSelect = null }) => (
  <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4">
    <div className="hidden md:block absolute top-[46px] left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
    {steps.map((s, i) => {
      const active = onSelect && activeIndex === i;
      const Tag = onSelect ? 'button' : 'div';
      return (
        <Tag
          key={i}
          type={onSelect ? 'button' : undefined}
          onClick={onSelect ? () => onSelect(i) : undefined}
          className={`${SUBCARD} relative p-6 text-center transition-all ${
            onSelect ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''
          } ${active ? 'ring-2 ring-red-500 border-red-300 dark:border-red-700 shadow-md' : ''}`}
        >
          <div className="w-11 h-11 mx-auto rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white flex items-center justify-center text-base font-bold shadow-md shadow-red-500/25">
            {i + 1}
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{s.title}</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{s.text}</p>
        </Tag>
      );
    })}
  </div>
);

export const ModalShell = ({ open, onClose, icon, emoji, tone = 'red', title, subtitle, children, footer, maxWidth = 'max-w-md', bodyClass = '' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`w-full ${maxWidth} max-h-[85vh] flex flex-col rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative flex flex-col items-center px-6 pt-6 pb-4 text-center">
          {(icon || emoji) && <IconTile icon={icon} emoji={emoji} tone={tone} size="lg" className="mb-3" />}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-6 pl-6">{title}</h3>
          {subtitle && <div className="mt-2">{subtitle}</div>}
          <button type="button" onClick={onClose} className={`absolute right-4 top-4 ${ICON_BTN}`} aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className={`px-6 pb-6 overflow-y-auto ${bodyClass}`}>{children}</div>
        {footer && <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
};
